/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from 'react';
import { useMutation, type FetchResult } from '@apollo/client';

import { handleFetchErrors } from '@/services/apollo';

import { FETCH_QUESTIONS, UPDATE_QUESTIONS } from '../Actions';
import CardModal from './CardModal';
import { addAnswerToPool, formatAnswer, processQueue } from './helpers';
import { circlesQuestion, dynamicQuestion, humanQuestion } from './questionHelpers';
import CardHeader from './CardHeader';
import CardBody from './CardBody';
import { cgStartCard } from './data';

interface SaveAnswerRequest {
    questionId: string,
    request: () => Promise<FetchResult<any>>,
}

const mapQuestionToData = (questionData) => {
    switch (questionData.questionType) {
    case 'circle':
        return circlesQuestion(questionData);
    case 'human':
        return humanQuestion(questionData);
    case 'primary_email':
    case 'name':
    case 'photo':
    case 'mapping':
    case 'birthday':
        return dynamicQuestion(questionData);
    default:
        return null;
    }
};

export const processQuestionsData = (rawQuestions) => {
    if (!rawQuestions.length) {
        return null;
    }

    let output = [...rawQuestions];

    output = output.map(mapQuestionToData).filter((question) => question !== null);

    return output;
};

function ContactsGame({ showHeader = true }) {
    const [gameStarted, setGameStarted] = useState(false);
    const [actualQuestion, setActualQuestion] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [time, setTime] = useState(60);
    const [isPaused, setIsPaused] = useState(false);
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [showReviewAnswers, setShowReviewAnswers] = useState(false);
    const [showNoQuestions, setShowNoQuestions] = useState(false);
    const [questionsData, setQuestionsData] = useState([]);
    const [requestQueue, setRequestQueue] = useState<SaveAnswerRequest[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorAmounts, setErrorAmounts] = useState(0);
    const [requestError, setRequestError] = useState(false);
    const [multipleErrorOnSending, setMultipleErrorOnSending] = useState(false);
    const [currentlySending, setCurrentlySending] = useState('');

    const startTimeRef = useRef(null);
    const rafIdRef = useRef(null);
    const elapsedTimeRef = useRef(0);
    const lastFrameAtRef = useRef(null);

    const [getQuestionsMutation] = useMutation(FETCH_QUESTIONS);
    const [answerQuestionMutation] = useMutation(UPDATE_QUESTIONS);

    useEffect(() => {
        if (errorAmounts >= 3) {
            setMultipleErrorOnSending(true);
        }
        if (!isProcessing && requestQueue.length > 0) {
            processQueue(
                setIsProcessing,
                requestQueue,
                setRequestQueue,
                setErrorAmounts,
                setCurrentlySending,
            );
        }
    }, [requestQueue, isProcessing, errorAmounts]);

    const addRequestToQueue = (request: SaveAnswerRequest) => {
        setRequestQueue((prevQueue) => [...prevQueue, request]);
    };

    const startGame = async () => {
        try {
            const response = await getQuestionsMutation({
                variables: {
                    input: {},
                },
            });
            const { questions }  = response.data.createContactGame.contactGame;
            if (questions.length === 0) {
                setShowNoQuestions(true);
                setGameStarted(true);
                setTime(0);
                setIsPaused(true);
                return;
            }
            setShowNoQuestions(false);
            setAnswers([]);
            setQuestionsData(processQuestionsData(questions));
            setGameStarted(true);
            setTime(60);
            setIsPaused(false);
            startTimeRef.current = null;
            elapsedTimeRef.current = 0;
            lastFrameAtRef.current = null;
        } catch (err) {
            handleFetchErrors(err);
            setRequestError(true);
        }
    };

    const answerQuestion = async (answer) => {
        const request = () => answerQuestionMutation({
            variables: {
                contactGameQuestion: answer,
            },
        });

        addRequestToQueue({ questionId: answer.id, request });
    };

    const pauseTimer = (value) => {
        setIsPaused(value);
        if (value) {
            cancelAnimationFrame(rafIdRef.current);
        } else {
            lastFrameAtRef.current = Date.now();
        }
    };

    useEffect(() => {
        let cleanupFunction = () => { };

        if (!gameStarted || !questionsData) {
            return cleanupFunction;
        }

        if (
            (multipleErrorOnSending || questionsData.length !== 0)
            && questionsData.length === actualQuestion
            && !isPaused
        ) {
            pauseTimer(true);
        } else if (
            !isPaused
            && questionsData.length !== 0
            && questionsData.length !== actualQuestion
        ) {
            const updateTimer = () => {
                const timeNow = Date.now();
                const deltaTime = timeNow - lastFrameAtRef.current;
                lastFrameAtRef.current = timeNow;
                elapsedTimeRef.current += deltaTime;

                const newTime = Math.max(60 - Math.floor(elapsedTimeRef.current / 1000), 0);
                setTime(newTime);

                if (newTime > 0) {
                    rafIdRef.current = requestAnimationFrame(updateTimer);
                }
            };

            if (!startTimeRef.current) {
                startTimeRef.current = Date.now();
                lastFrameAtRef.current = startTimeRef.current;
            }
            rafIdRef.current = requestAnimationFrame(updateTimer);

            cleanupFunction = () => cancelAnimationFrame(rafIdRef.current);
        }

        return cleanupFunction;
    }, [gameStarted, isPaused, actualQuestion, multipleErrorOnSending]);

    const saveQuestionData = (answer, question) => {
        if (isPaused && !question) {
            pauseTimer(false);
        }
        const actualAnswers = [...answers];
        const formatedAnswer = formatAnswer(
            question || questionsData[actualQuestion],
            answer,
            false,
        );
        setAnswers(addAnswerToPool(actualAnswers, formatedAnswer.toReview));
        answerQuestion(formatedAnswer.toSend);
        if (!question) {
            setActualQuestion(actualQuestion + 1);
        }
    };

    const closeModal = () => {
        if (rafIdRef.current) {
            cancelAnimationFrame(rafIdRef.current);
        }
        setGameStarted(false);
        setActualQuestion(0);
        setAnswers([]);
        setTime(60);
        setIsPaused(false);
        setShowWarningModal(false);
        setShowReviewAnswers(false);
        setShowNoQuestions(false);
        setRequestQueue([]);
        setIsProcessing(false);
        setRequestError(false);
        setMultipleErrorOnSending(false);
        setCurrentlySending('');
        startTimeRef.current = null;
        rafIdRef.current = null;
        elapsedTimeRef.current = 0;
        lastFrameAtRef.current = null;
    };

    const showClosingModal = () => {
        setShowWarningModal(!showWarningModal);
    };

    const toggleShowReviewAnswers = () => {
        setShowReviewAnswers(true);
    };

    const skipQuestion = () => {
        if (isPaused) {
            pauseTimer(false);
        }
        const actualAnswers = [...answers];
        const formatedAnswer = formatAnswer(questionsData[actualQuestion], null, true);
        setAnswers(addAnswerToPool(actualAnswers, formatedAnswer.toReview));
        answerQuestion(formatedAnswer.toSend);
        setActualQuestion(actualQuestion + 1);
    };

    const prevQuestion = () => {
        if (isPaused) {
            pauseTimer(false);
        }
        setActualQuestion(actualQuestion - 1);
    };

    const toggleEndGame = (value) => {
        if (value === '1') {
            closeModal();
        } else {
            setGameStarted(false);
            setActualQuestion(0);
            setTime(60);
            setIsPaused(false);
            startTimeRef.current = null;
            elapsedTimeRef.current = 0;
            lastFrameAtRef.current = null;
        }
    };

    const noMoreQuestions = !questionsData
        || (questionsData.length !== 0 && questionsData.length === actualQuestion);

    return (
        gameStarted ? (
            <CardModal
                isModalOpen
                data={questionsData}
                startGame={startGame}
                skipQuestion={skipQuestion}
                showNoQuestions={showNoQuestions}
                showClosingModal={showClosingModal}
                showWarningModal={showWarningModal}
                closeModal={closeModal}
                noMoreQuestions={noMoreQuestions}
                actualQuestion={actualQuestion}
                saveQuestionData={saveQuestionData}
                toggleEndGame={toggleEndGame}
                prevQuestion={prevQuestion}
                time={time}
                pauseTimer={pauseTimer}
                isPaused={isPaused}
                requestError={requestError}
                multipleErrorOnSending={multipleErrorOnSending}
                toggleShowReviewAnswers={toggleShowReviewAnswers}
                showReviewAnswers={showReviewAnswers}
                answers={answers}
                currentlySending={currentlySending}
            />
        ) : (
            <div className="relative flex flex-col items-center justify-center w-full bg-transparent rounded-xl">
                {showHeader && (
                    <CardHeader
                        data={questionsData[actualQuestion]}
                        startCard
                    />
                )}
                <CardBody
                    data={cgStartCard}
                    gameStarted={false}
                    startGame={startGame}
                    skipQuestion={skipQuestion}
                    saveQuestionData={saveQuestionData}
                    noMoreQuestions={false}
                    toggleEndGame={toggleEndGame}
                    toggleShowReviewAnswers={toggleShowReviewAnswers}
                />
            </div>
        )
    );
}

export default ContactsGame;
